<template>
  <div>
    <md-content class='ppi_list__wrapper'>
      <div class="md-layout md-gutter header-section">
        <div class="md-layout-item">
          <span class="md-title">Vos actions programmées</span>
        </div>
        <div class="md-layout-item import-section-header">
          <div class="import-actions">
            <md-button
              @click="openImportDialog()"
              class="md-icon-button md-raised md-primary">
              <md-icon>file_upload</md-icon>
              <md-tooltip md-direction="left">Importer un fichier Excel</md-tooltip>
            </md-button>
          </div>
        </div>
      </div>

      <!-- Tabs for PPI Collections -->
      <div class="tabs-with-button">
        <md-tabs
          @md-changed="collectionTabChanged"
          class="ppi-collections-tabs"
          :md-active-tab="activeCollectionTab">
          <md-tab
            v-for="collection in sortedPPICollections"
            :key="`collection-${collection.id}`"
            :id="`tab-collection-${collection.id}`"
            :md-label="collection.name" />
        </md-tabs>
        <md-button
          @click="showCreateCollectionDialog = true"
          class="md-icon-button md-raised add-collection-button">
          <md-icon>add</md-icon>
          <md-tooltip md-direction="left">Créer une nouvelle liste PPI</md-tooltip>
        </md-button>
        <md-button
          v-if="activeCollection && !isMainCollection(activeCollection)"
          @click="showRenameCollectionDialog = true; collectionToRename = activeCollection; renamedCollection.name = activeCollection.name"
          class="md-icon-button md-raised rename-collection-button">
          <md-icon>edit</md-icon>
          <md-tooltip md-direction="left">Renommer la liste "{{ activeCollection.name }}"</md-tooltip>
        </md-button>
        <md-button
          v-if="activeCollection && !isMainCollection(activeCollection)"
          @click="showDeleteCollectionDialog = true; collectionToDelete = activeCollection"
          class="md-icon-button md-raised md-danger delete-collection-button">
          <md-icon>delete</md-icon>
          <md-tooltip md-direction="left">Supprimer la liste "{{ activeCollection.name }}"</md-tooltip>
        </md-button>
      </div>

      <div class="box ">
        <md-table
          v-model="filteredAfterWorkPPI"
          class="zebra">
          <md-table-empty-state
            md-label="Aucune action programmée"
            md-description="Aucune action définie, veuillez utiliser le formulaire ci-dessous pour en ajouter" />
          <md-table-row
            slot="md-table-row"
            slot-scope="{ item }">
            <md-table-cell
              md-label="Action">
              <strong>{{ item.action_label }}</strong>
              <div><em>{{ item.action_sub_category_label }}</em></div>
            </md-table-cell>
            <md-table-cell
              md-label="Appliqué à">
              <strong>
                <div>
                  {{ item.place_details.name }} ({{ item.place_details.identifier }})
                </div>
              </strong>
            </md-table-cell>
            <md-table-cell
              md-label="Année">
              <strong>{{ item.year }}</strong>
            </md-table-cell>
            <md-table-cell
              md-label="Coût énergie">
              {{ item.energy_investment_display }}
            </md-table-cell>
            <md-table-cell
              md-label="Coût global">
              {{ item.total_investment_display }}
            </md-table-cell>
            <md-table-cell
              md-label="Économie estimée *">
              {{ item.economy_display }}
            </md-table-cell>
            <md-table-cell
              md-label="Temps de retour estimé **">
              {{ item.return_time_display }}
            </md-table-cell>
            <md-table-cell
              md-label="Commentaires">
              {{ item.comment }}
            </md-table-cell>
            <md-table-cell>
              <md-button
                @click="fillAfterWorkPPIForms(item)"
                class="md-primary">
                Modifier
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
        <div class='md-caption'><sup>*</sup> <em>L'économie est calculée à coût constant pour l'énergie, elle est donc à priori sous-évaluée</em></div>
        <div class='md-caption'><sup>**</sup> <em>Même observation que pour l'économie, le temps de retour réel sera à priori moindre</em></div>
      </div>

      <span class="md-title">Nouvelle action programmée</span>
      <div class="box">
        <div class="md-layout md-gutter  flex-end">
          <div class="md-layout-item">
            <md-field
              :class="{ 'md-invalid': $v.newAfterWorkPPI.selectedSite.$error, 'md-has-value': true, 'md-required': true }">
              <label for="site">Site</label>
              <multiselect
                name="site"
                v-model="newAfterWorkPPI.selectedSite"
                :options="userSites"
                :preserve-search="true"
                :custom-label="placeChoiceName"
                placeholder="Sélectionnez ou entrez le nom d'un site"
                track-by="id"
                select-label="Sélectionner"
                select-group-label="Sélectionner le groupe"
                selected-label="Sélectionné ✓"
                deselect-label="Désélectionner ✗"
                deselect-group-label="Désélectionner le groupe ✗"
                :max-height="280">
                <span slot="noResult">Aucun site trouvé, veuillez modifier votre recherche.</span>
              </multiselect>
              <span
                class="md-error"
                v-if="!$v.newAfterWorkPPI.selectedSite.required">Ce champ est obligatoire</span>
            </md-field>
          </div>
          <div class="md-layout-item">
            <md-field
              :class="{ 'md-invalid': $v.newAfterWorkPPI.selectedAreas.$error, 'md-has-value': true, 'md-required': true }">
              <label for="area">Zones concernées</label>
              <multiselect
                name="area"
                v-model="newAfterWorkPPI.selectedAreas"
                :options="areaList"
                group-values="areas"
                group-label="buildingName"
                :group-select="true"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="placeChoiceName"
                track-by="id"
                placeholder="Sélectionnez ou entrez le nom d'une zone concernée par l'action"
                select-label="Sélectionner"
                select-group-label="Sélectionner le groupe"
                selected-label="Sélectionné ✓"
                deselect-label="Désélectionner ✗"
                deselect-group-label="Désélectionner le groupe ✗">
                <span slot="noResult">Aucun lieu trouvé, veuillez modifier votre recherche.</span>
              </multiselect>
              <span
                class="md-error"
                v-if="!$v.newAfterWorkPPI.selectedAreas.required">Ce champ est obligatoire</span>
              <span
                class="md-error"
                v-if="!$v.newAfterWorkPPI.selectedAreas.similarPPIDoesntExist">Il existe un PPI défini avec des paramètres similaires. Veuillez modifier le PPI existant.</span>
            </md-field>
          </div>
        </div>
        <div class="md-layout md-gutter flex-end">
          <div class="md-layout-item flex-end">
            <md-field
              :class="{ 'md-has-value': true }">
              <label for="action_category">Catégorie d'action</label>
              <md-select
                md-dense
                v-model="newAfterWorkPPI.selectedActionCategory"
                md-class="md-menu-content-very-huge"
                name="action_category"
                id="action_category"
                placeholder="Choisissez une catégorie d'action">
                <md-optgroup
                  v-for="actionsCategory in actionsCategories"
                  :label="actionsCategory.label"
                  :key="actionsCategory.slug">
                  <md-option
                    :key="actionsSubCategory.slug"
                    v-for="actionsSubCategory in actionsCategory.sub_categories"
                    :value="actionsSubCategory.id">
                    {{ actionsSubCategory.label }}
                  </md-option>
                </md-optgroup>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item flex-end">
            <md-field
              :class="{ 'md-invalid': $v.newAfterWorkPPI.selectedAction.$error, 'md-has-value': true }">
              <label for="action">Action</label>
              <md-select
                md-dense
                md-class="md-menu-content-very-huge"
                v-model="newAfterWorkPPI.selectedAction"
                name="action"
                id="action"
                placeholder="Choisissez une action"
                required>
                <md-option
                  v-for="action in actionList"
                  :key="action.id"
                  :value="action.id">
                  {{ action.label }}
                </md-option>
              </md-select>
              <span
                class="md-error"
                v-if="!$v.newAfterWorkPPI.selectedAction.required">Ce champ est obligatoire</span>
            </md-field>
          </div>
          <div class="md-layout-item">
            <md-field>
              <label for="year">Année</label>
              <md-select
                v-model="newAfterWorkPPI.selectedYear"
                name="year"
                required>
                <md-option
                  v-for="year in years"
                  :key='year'
                  :value="year">{{ year }}</md-option>
              </md-select>
            </md-field>
          </div>
        </div>
        <md-button
          @click="cleanNewAfterWorkPPIForm()"
          class="md-raised md-primary md-dense">
          Ajouter l'action
        </md-button>
      </div>

    </md-content>

    <!-- Import options dialog -->
    <md-dialog :md-active.sync="showImportDialog">
      <md-dialog-title>Importer un fichier Excel</md-dialog-title>
      <md-dialog-content>
        <div style="margin-bottom: 1.5rem;">
          <md-button
            @click="downloadTemplate()"
            class="md-raised">
            <md-icon>file_download</md-icon>
            Télécharger le template
          </md-button>
        </div>
        <div style="margin-bottom: 1.5rem;">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx"
            @change="handleFileSelect"
            style="display: none">
          <md-button
            @click="$refs.fileInput.click()"
            class="md-raised md-primary">
            <md-icon>file_upload</md-icon>
            Sélectionner un fichier Excel
          </md-button>
          <div
            v-if="pendingFile"
            style="margin-top: 0.5rem; color: #4caf50;">
            <md-icon>check_circle</md-icon>
            Fichier sélectionné : {{ pendingFile.name }}
          </div>
        </div>
        <div v-if="pendingFile">
          <md-field>
            <label>Liste PPI</label>
            <md-select
              v-model="importOptions.ppiCollectionId"
              name="ppi_collection">
              <md-option
                v-for="collection in ppiCollections"
                :key="`import-collection-${collection.id}`"
                :value="collection.id">
                {{ collection.name }}
              </md-option>
            </md-select>
          </md-field>
          <md-checkbox
            v-model="importOptions.deleteExisting"
            id="delete-existing-checkbox">
            Supprimer les PPI existants avant l'import
          </md-checkbox>
          <div
            v-if="importOptions.deleteExisting && importOptions.ppiCollectionId"
            class="md-caption">
            <em>Seuls les PPI de la liste sélectionnée seront supprimés</em>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button
          @click="closeImportDialog()">
          Annuler
        </md-button>
        <md-button
          v-if="pendingFile"
          class="md-primary"
          @click="confirmImport()">
          Importer
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Create collection dialog -->
    <md-dialog
      :md-active.sync="showCreateCollectionDialog"
      class="create-collection-dialog">
      <md-dialog-title>Créer une nouvelle liste PPI</md-dialog-title>
      <md-dialog-content>
        <md-field>
          <label>Nom de la liste</label>
          <md-input
            v-model="newCollection.name"
            required />
        </md-field>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button
          @click="showCreateCollectionDialog = false">
          Annuler
        </md-button>
        <md-button
          class="md-primary"
          @click="createCollection()"
          :disabled="!newCollection.name">
          Créer
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Rename collection dialog -->
    <md-dialog :md-active.sync="showRenameCollectionDialog">
      <md-dialog-title>Renommer la liste PPI</md-dialog-title>
      <md-dialog-content>
        <md-field>
          <label>Nom de la liste</label>
          <md-input
            v-model="renamedCollection.name"
            required />
        </md-field>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button
          @click="showRenameCollectionDialog = false">
          Annuler
        </md-button>
        <md-button
          class="md-primary"
          @click="renameCollection()"
          :disabled="!renamedCollection.name || (collectionToRename && renamedCollection.name === collectionToRename.name)">
          Renommer
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Delete collection dialog -->
    <md-dialog :md-active.sync="showDeleteCollectionDialog">
      <md-dialog-title>Supprimer la liste PPI</md-dialog-title>
      <md-dialog-content>
        <p>
          Êtes-vous sûr de vouloir supprimer la liste
          <strong>"{{ collectionToDelete ? collectionToDelete.name : '' }}"</strong> ?
        </p>
        <p class="warning-text">
          <md-icon>warning</md-icon>
          Cette action est irréversible. Toutes les actions PPI de cette liste seront également supprimées.
        </p>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button
          @click="showDeleteCollectionDialog = false">
          Annuler
        </md-button>
        <md-button
          class="md-accent"
          @click="deleteCollection()">
          Supprimer
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Import errors dialog -->
    <md-dialog :md-active.sync="showImportErrorsDialog">
      <md-dialog-title>
        <md-icon
          style="color: #f44336; vertical-align: middle; margin-right: 8px;">error</md-icon>
        {{ importErrors.title }}
      </md-dialog-title>
      <md-dialog-content>
        <div
          v-if="importErrors.importedCount > 0"
          style="margin-bottom: 1rem; padding: 0.75rem; background-color: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
          <md-icon
            style="color: #ff9800; vertical-align: middle; margin-right: 8px;">info</md-icon>
          <strong>{{ importErrors.importedCount }} action(s) importée(s) malgré les erreurs.</strong>
        </div>
        <div v-if="importErrors.errors && importErrors.errors.length > 0">
          <h4
            style="margin-top: 0; margin-bottom: 1rem;">Détails des erreurs :</h4>
          <ul
            style="margin: 0; padding-left: 1.5rem;">
            <li
              v-for="(error, index) in importErrors.errors"
              :key="`error-${index}`"
              style="margin-bottom: 0.5rem; line-height: 1.5;">
              {{ error }}
            </li>
          </ul>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button
          class="md-primary"
          @click="showImportErrorsDialog = false">
          Fermer
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="afterWorkPPIForms.showPPIDialog">
      <md-dialog-title>Paramétrage de l'action</md-dialog-title>
      <md-dialog-content>
        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <md-field>
              <label>Site</label>
              <md-input
                :value="afterWorkPPIForms.site.displayName"
                type="text"
                name="site"
                disabled />
            </md-field>
          </div>
          <div class="md-layout-item">
            <md-field>
              <label>Action</label>
              <md-input
                :value="afterWorkPPIForms.action.label"
                type="text"
                name="action"
                disabled />
            </md-field>
          </div>
          <div class="md-layout-item">
            <md-field>
              <label>Année de réalisation de l'action</label>
              <md-select
                v-model="afterWorkPPIForms.year"
                name="year"
                required>
                <md-option
                  v-for="year in years"
                  :key='year'
                  :value="year">{{ year }}</md-option>
              </md-select>
            </md-field>
          </div>
        </div>

        <h3 class="">Coûts et consommations estimées après réalisation de l'action</h3>
        <span class="bold">Bâtiments :</span>&nbsp;
        <md-card
          class="dashed__card md-elevation-0"
          v-for='building in $v.afterWorkPPIForms.buildings.$each.$iter'
          :key="`building-${building.$model.id}`">
          <md-card-content>

            <div>

              <span>{{ building.$model.displayName }}</span>
              <div>

                <div
                  v-for='area in building.areas.$each.$iter'
                  :key="`area-${area.$model.id}`">
                  <span class="bold">Zones :</span>&nbsp;
                  <span>{{ area.$model.displayName }}</span>
                  <div class="md-layout md-gutter">
                    <div
                      class="md-layout-item">
                      <md-field
                        :class="{ 'md-invalid': area.energy_investment.$error }">
                        <label for="energy_investment">Coût énergie</label>
                        <md-input
                          :value="formatNumericForInput(area.energy_investment.$model)"
                          @input="area.energy_investment.$model = normalizeNumericValue($event.target.value)"
                          type="text"
                          name="energy_investment"
                          required />
                        <span
                          class="md-error"
                          v-if="!area.energy_investment.required">Ce champ est requis</span>
                        <span
                          class="md-error"
                          v-if="!area.energy_investment.minValue">Saisissez une valeur positive</span>
                      </md-field>
                    </div>
                    <div
                      class="md-layout-item">
                      <md-field
                        :class="{ 'md-invalid': area.total_investment.$error }">
                        <label for="total_investment">Coût total</label>
                        <md-input
                          :value="formatNumericForInput(area.total_investment.$model)"
                          @input="area.total_investment.$model = normalizeNumericValue($event.target.value)"
                          type="text"
                          name="total_investment"
                          required />
                        <span
                          class="md-error"
                          v-if="!area.total_investment.required">Ce champ est requis</span>
                        <span
                          class="md-error"
                          v-if="!area.total_investment.minValue">Saisissez une valeur positive</span>
                      </md-field>
                    </div>
                  </div>

                  <div
                    class="md-layout md-gutter"
                    v-for='energy in area.energies.$each.$iter'
                    :key="`area-${area.$model.id}-energy-${energy.$model.id}`">
                    <div
                      class="md-layout-item">
                      <span class="bold"> Énergie </span> <br>
                      {{ energy.$model.label }}
                    </div>
                    <div
                      class="md-layout-item">
                      <span class="bold">{{ area.$model.consumptions_year }}</span>
                      <br>
                      <em> {{ energy.$model.total_energy_consumption }} kWh<em>EF</em></em>
                    </div>
                    <div class="md-layout-item">
                      <md-field :class="{ 'md-invalid': energy.after_work_energy_consumption.$error }">
                        <label for="first-name">Après travaux</label>
                        <md-input
                          :value="formatNumericForInput(energy.after_work_energy_consumption.$model)"
                          @input="energy.after_work_energy_consumption.$model = normalizeNumericValue($event.target.value)"
                          type="text"
                          name="after_work_energy_consumption" />
                        <span class="md-suffix">kWh<em>EF/an</em></span>
                        <span
                          class="md-error"
                          v-if="!energy.after_work_energy_consumption.minValue">Saisissez une valeur positive</span>
                        <span
                          class="md-error"
                          v-if="!energy.after_work_energy_consumption.required">Ce champ est requis</span>
                      </md-field>

                    </div>
                  </div>
                  <div class="md-layout md-gutter">
                    <div
                      class="md-layout-item">
                      <md-field>
                        <label>Commentaires</label>
                        <md-textarea v-model="area.$model.comment"/>
                        <md-icon>description</md-icon>
                      </md-field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </md-card-content>
        </md-card>
        <md-snackbar
          :md-position="'center'"
          :md-active.sync="afterWorkPPIForms.showSnackbar">
          <span>Veuillez corriger les erreurs puis ré-essayer</span>
        </md-snackbar>
      </md-dialog-content>

      <md-dialog-actions class="dialog-actions">
        <div class="md-layout-item dialog-action-left">
          <md-button
            class="md-accent"
            v-if='afterWorkPPIForms.id'
            @click="deleteAfterWorkPPI()">Supprimer</md-button>
        </div>
        <div class="md-layout-item dialog-action-right">
          <md-button
            @click="cancelAfterWorkPPIForms()">Annuler</md-button>
          <md-button
            class="md-primary"
            @click="cleanAfterWorkPPIForm()">Valider</md-button>
        </div>
      </md-dialog-actions>
    </md-dialog>

  </div>
</template>

<script>
import session from '../api/session'
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import { validationMixin } from 'vuelidate'
import { required, minValue } from 'vuelidate/lib/validators'
import ValuesDisplayMixin from '../mixins/ValuesDisplayMixin'
import FileDownload from 'js-file-download'

function similarPPIDoesntExist (value) {
  var app = this
  var selectedAction = app.newAfterWorkPPI.selectedAction
  var selectedYear = app.newAfterWorkPPI.selectedYear
  var similarExist = false

  app.newAfterWorkPPI.selectedAreas.forEach(function (area) {
    var similarPPI = app.afterWorkPPI.filter(afterWorkPPI => afterWorkPPI.place === area.id && afterWorkPPI.action === selectedAction && afterWorkPPI.year === selectedYear)

    if (similarPPI.length > 0) {
      similarExist = true
    }
  })
  return similarExist === false
}

export default {
  name: 'PPIList',
  mixins: [validationMixin, ValuesDisplayMixin],
  components: {
    Multiselect
  },
  data () {
    return {
      energyList: [],
      actionsPlaceType: {},
      actionsCategories: [],
      actions: [],
      userSites: [],
      userPlaces: [],
      afterWorkPPI: [],
      filteredAfterWorkPPI: [],
      ppiCollections: [],
      activeCollectionTab: null,
      mainCollectionId: null,
      years: [],

      newAfterWorkPPI: {
        selectedSite: null,
        selectedActionCategory: null,
        selectedAction: null,
        selectedAreas: [],
        selectedYear: new Date().getFullYear()
      },

      afterWorkPPIForms: {
        id: null,
        site: {},
        year: null,
        action: {},
        buildings: [],
        showPPIDialog: false,
        showSnackbar: false
      },
      importResult: null,
      importOptions: {
        deleteExisting: false,
        ppiCollectionId: null
      },
      showImportDialog: false,
      showCreateCollectionDialog: false,
      showDeleteCollectionDialog: false,
      showRenameCollectionDialog: false,
      showImportErrorsDialog: false,
      importErrors: {
        title: '',
        errors: [],
        importedCount: 0
      },
      collectionToDelete: null,
      collectionToRename: null,
      newCollection: {
        name: ''
      },
      renamedCollection: {
        name: ''
      },
      pendingFile: null
    }
  },
  mounted: function () {
    this.$store.dispatch('routing/update_title', 'PPI')
    this.$store.dispatch('routing/update_return_kwargs', null)
    this.loadApi()
  },
  validations () {
    var validations = {
      newAfterWorkPPI: {
        selectedSite: {
          required
        },
        selectedAction: {
          required
        },
        selectedAreas: {
          required,
          similarPPIDoesntExist
        },
        selectedYear: {
          required
        }
      },
      afterWorkPPIForms: {
        buildings: {
          $each: {
            areas: {
              $each: {
                energy_investment: {
                  required,
                  minValue: minValue(0)
                },
                total_investment: {
                  required,
                  minValue: minValue(0)
                },
                energies: {
                  $each: {
                    after_work_energy_consumption: {
                      required,
                      minValue: minValue(0)
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return validations
  },
  computed: {
    actionList () {
      var app = this
      var actionsList = []
      var selectedActionCategory = app.newAfterWorkPPI.selectedActionCategory
      var selectedAction = app.newAfterWorkPPI.selectedAction

      for (var i in app.actionsCategories) {
        var subCategories = app.actionsCategories[i].sub_categories
        for (var j in subCategories) {
          var actions = subCategories[j].actions
          for (var k in actions) {
            if (selectedActionCategory === null || selectedActionCategory === subCategories[j].id) {
              actionsList.push(actions[k])
            }
          }
        }
      }

      if (selectedAction !== null) {
        var actionInList = actionsList.findIndex(function (a) { return a.id === selectedAction }) !== -1

        if (actionInList === false) {
          app.newAfterWorkPPI.selectedAction = null
        }
      }

      if (selectedActionCategory !== null && selectedAction === null && actionsList.length === 1) {
        app.newAfterWorkPPI.selectedAction = actionsList[0].id
      }

      return actionsList
    },
    areaList () {
      var app = this
      var selectedSite = app.newAfterWorkPPI.selectedSite
      var buildings = []
      var areaList = []

      if (selectedSite) {
        buildings = app.userPlaces.filter(place => place.parent === selectedSite.id)
      } else {
        buildings = app.userPlaces.filter(place => place.type === 'building')
      }

      buildings.forEach(function (building) {
        var areaGroup = {
          buildingName: app.placeChoiceName(building),
          areas: app.userPlaces.filter(place => place.parent === building.id && place.type === 'area')
        }
        areaList.push(areaGroup)
      })

      app.newAfterWorkPPI.selectedAreas = []

      if (buildings.length === 1) {
        if (areaList[0].areas.length === 1) {
          app.newAfterWorkPPI.selectedAreas = [areaList[0].areas[0]]
        }
      }

      return areaList
    },
    sortedPPICollections: function () {
      var app = this
      // Sort collections: "Liste principale" first, then others alphabetically
      var sorted = app.ppiCollections.slice().sort(function (a, b) {
        if (a.name === 'Liste principale') return -1
        if (b.name === 'Liste principale') return 1
        return a.name.localeCompare(b.name)
      })
      return sorted
    },
    activeCollection: function () {
      var app = this
      if (!app.activeCollectionTab || !app.activeCollectionTab.startsWith('tab-collection-')) {
        return null
      }
      var collectionId = parseInt(app.activeCollectionTab.replace('tab-collection-', ''))
      return app.ppiCollections.find(function (col) {
        return col.id === collectionId
      })
    }
  },
  methods: {
    cleanNewAfterWorkPPIForm () {
      var app = this
      app.$v.newAfterWorkPPI.$touch()
      if (!app.$v.newAfterWorkPPI.$invalid) {
        app.fillAfterWorkPPIForms(null)
      }
    },
    fillAfterWorkPPIForms (afterWorkPPI) {
      var app = this

      app.afterWorkPPIForms.buildings = []
      var buildingIds = []
      var areaIds = []
      var areas = []

      if (afterWorkPPI) {
        var area = app.getUserPlace(afterWorkPPI.place)
        var building = app.getUserPlace(area.parent)
        var site = app.getUserPlace(building.parent)
        app.afterWorkPPIForms.id = afterWorkPPI.id
        app.afterWorkPPIForms.site = site
        app.afterWorkPPIForms.year = afterWorkPPI.year
        app.afterWorkPPIForms.action = app.getAction(afterWorkPPI.action)

        areas.push(area)
        areaIds.push(area.id)
        buildingIds.push(building.id)
      } else {
        app.afterWorkPPIForms.id = null
        app.afterWorkPPIForms.site = app.newAfterWorkPPI.selectedSite
        app.afterWorkPPIForms.year = app.newAfterWorkPPI.selectedYear
        app.afterWorkPPIForms.action = app.getAction(app.newAfterWorkPPI.selectedAction)

        app.newAfterWorkPPI.selectedAreas.forEach(function (selectedArea) {
          areas.push(selectedArea)
          areaIds.push(selectedArea.id)
          if (!(buildingIds.includes(selectedArea.parent))) {
            buildingIds.push(selectedArea.parent)
          }
        })
      }

      // Retrieve consumptions for selected areas
      session.get('/api/userdata/place-energy-consumptions/', {params: {'places': areaIds}}).then(function (response) {
        var areasConsumptions = response.data

        buildingIds.forEach(function (buildingId) {
          var building = app.getUserPlace(buildingId)

          var buildingAreas = areas.filter(area => area.parent === buildingId)

          app.$set(building, 'areas', [])

          buildingAreas.forEach(function (area) {
            app.$set(area, 'energy_investment', null)
            app.$set(area, 'total_investment', null)
            app.$set(area, 'energies', [])
            app.$set(area, 'comment', null)

            if (afterWorkPPI) {
              // Convert values to strings with dots (for vuelidate numeric validator)
              // vuelidate numeric accepts both numbers and numeric strings
              var energyInvestment = afterWorkPPI.energy_investment
              var totalInvestment = afterWorkPPI.total_investment

              // Convert numbers to strings with dots
              if (typeof energyInvestment === 'number') {
                energyInvestment = energyInvestment.toString()
              }
              if (typeof totalInvestment === 'number') {
                totalInvestment = totalInvestment.toString()
              }

              app.$set(area, 'energy_investment', app.normalizeNumericValue(energyInvestment))
              app.$set(area, 'total_investment', app.normalizeNumericValue(totalInvestment))
              app.$set(area, 'comment', afterWorkPPI.comment)
            }

            var areaConsumptions = areasConsumptions.filter(areaConsumption => areaConsumption.place === area.id)

            app.$set(area, 'consumptions_year', areaConsumptions[0].year)

            areaConsumptions.forEach(function (areaConsumption) {
              var energy = app.getEnergy(areaConsumption.energy)
              app.$set(energy, 'total_energy_consumption', areaConsumption.total_energy_consumption)
              app.$set(energy, 'after_work_energy_consumption', null)

              if (afterWorkPPI) {
                var afterWorkEnergyConsumption = afterWorkPPI.after_work_energies.filter(afterWorkEnergy => afterWorkEnergy.energy === energy.id)

                if (afterWorkEnergyConsumption.length > 0) {
                  // Convert value to string with dots (for vuelidate numeric validator)
                  var energyConsumption = afterWorkEnergyConsumption[0].energy_consumption
                  if (typeof energyConsumption === 'number') {
                    energyConsumption = energyConsumption.toString()
                  }
                  app.$set(energy, 'after_work_energy_consumption', app.normalizeNumericValue(energyConsumption))
                }
              }
              area.energies.push(energy)
            })
            building.areas.push(area)
          })
          app.afterWorkPPIForms.buildings.push(building)
        })

        app.afterWorkPPIForms.showPPIDialog = true
      })
    },
    normalizeNumericValue: function (value) {
      // Convert string values with commas/spaces to number (for vuelidate numeric validator)
      // vuelidate numeric validator expects a number type or a numeric string
      if (value === null || value === undefined || value === '') {
        return null
      }
      if (typeof value === 'number') {
        return isNaN(value) ? null : value
      }
      if (typeof value === 'string') {
        // Remove all spaces and replace all commas with dots
        var normalized = value.toString().trim().replace(/\s/g, '').replace(/,/g, '.')
        // Remove any non-numeric characters except dots and minus sign
        normalized = normalized.replace(/[^\d.-]/g, '')
        // Return null if empty or invalid
        if (normalized === '' || normalized === '-' || normalized === '.') {
          return null
        }
        var parsed = parseFloat(normalized)
        // Return the parsed number (not string) for vuelidate numeric validator
        return isNaN(parsed) ? null : parsed
      }
      var parsedValue = parseFloat(value)
      return isNaN(parsedValue) ? null : parsedValue
    },
    formatNumericForInput: function (value) {
      // Format number for display in input field (convert to string with dot)
      if (value === null || value === undefined) {
        return ''
      }
      if (typeof value === 'number') {
        return value.toString().replace(',', '.')
      }
      if (typeof value === 'string') {
        // Already a string, just ensure it uses dot not comma
        return value.toString().replace(',', '.')
      }
      return String(value).replace(',', '.')
    },
    cleanAfterWorkPPIForm () {
      var app = this
      app.$v.afterWorkPPIForms.$touch()

      if (!app.$v.afterWorkPPIForms.$invalid) {
        app.postAfterWorkPPIForms()
        app.afterWorkPPIForms.showSnackbar = false
      } else {
        app.afterWorkPPIForms.showSnackbar = true
      }
    },
    postAfterWorkPPIForms: function () {
      var app = this

      var action = app.afterWorkPPIForms.action
      var year = app.afterWorkPPIForms.year
      var formsData = []

      // Get active collection ID (or main collection as fallback)
      var activeCollectionId = app.mainCollectionId
      if (app.activeCollectionTab && app.activeCollectionTab.startsWith('tab-collection-')) {
        activeCollectionId = parseInt(app.activeCollectionTab.replace('tab-collection-', ''))
      }
      if (!activeCollectionId && app.ppiCollections.length > 0) {
        var mainCollection = app.ppiCollections.find(function (col) {
          return col.name === 'Liste principale'
        })
        if (mainCollection) {
          activeCollectionId = mainCollection.id
        } else {
          activeCollectionId = app.ppiCollections[0].id
        }
      }

      app.afterWorkPPIForms.buildings.forEach(function (building) {
        building.areas.forEach(function (area) {
          // Convert string values to numbers before sending to backend
          var totalInvestment = area.total_investment
          var energyInvestment = area.energy_investment

          // Convert to number if it's a string
          if (typeof totalInvestment === 'string') {
            totalInvestment = parseFloat(totalInvestment.replace(/,/g, '.')) || null
          }
          if (typeof energyInvestment === 'string') {
            energyInvestment = parseFloat(energyInvestment.replace(/,/g, '.')) || null
          }

          var afterWorkPPI = {
            'place': area.id,
            'action': action.id,
            'year': year,
            'comment': area.comment,
            'total_investment': totalInvestment,
            'energy_investment': energyInvestment,
            'ppi_collection': activeCollectionId,
            'after_work_energies': []
          }

          area.energies.forEach(function (energy) {
            var energyConsumption = energy.after_work_energy_consumption
            if (typeof energyConsumption === 'string') {
              energyConsumption = parseFloat(energyConsumption.replace(/,/g, '.')) || null
            }
            afterWorkPPI['after_work_energies'].push({
              'energy': energy.id,
              'energy_consumption': energyConsumption
            })
          })

          if (app.afterWorkPPIForms.id) {
            formsData = afterWorkPPI
          } else {
            formsData.push(afterWorkPPI)
          }
        })
      })

      var promeseResult = null

      app.$store.dispatch('loading/begin_loading')

      if (app.afterWorkPPIForms.id) {
        var ppiId = app.afterWorkPPIForms.id
        promeseResult = session.put(`/api/userdata/ppi/${ppiId}/`, formsData)
      } else {
        promeseResult = session.post(`/api/userdata/ppi/`, formsData)
      }

      promeseResult.then(function (response) {
        if (app.afterWorkPPIForms.id) {
          var afterWorkPPIIndex = app.afterWorkPPI.findIndex(function (s) { return s.id === response.data.id })
          app.$set(app.afterWorkPPI, afterWorkPPIIndex, response.data)
        } else {
          app.afterWorkPPI = app.afterWorkPPI.concat(response.data)
        }

        app.resetAfterWorkPPIForms()
        app.resetNewAfterWorkPPI()
      }).catch(function (error) {
        var response = error.response
        if (response.status === 500) {
          app.afterWorkPPIForms.showPPIDialog = false
        }
      }).then(function (response) {
        app.$store.dispatch('loading/end_loading')
      })
    },
    resetNewAfterWorkPPI: function () {
      var app = this
      app.newAfterWorkPPI.selectedSite = null
      app.newAfterWorkPPI.selectedActionCategory = null
      app.newAfterWorkPPI.selectedAction = null
      app.newAfterWorkPPI.selectedAreas = []
      app.newAfterWorkPPI.selectedYear = new Date().getFullYear()
      app.$v.newAfterWorkPPI.$reset()
    },
    resetAfterWorkPPIForms: function () {
      var app = this
      app.afterWorkPPIForms.id = null
      app.afterWorkPPIForms.site = {}
      app.afterWorkPPIForms.year = null
      app.afterWorkPPIForms.action = {}
      app.afterWorkPPIForms.buildings = []
      app.afterWorkPPIForms.showPPIDialog = false
      app.afterWorkPPIForms.showSnackbar = false
      app.$v.afterWorkPPIForms.$reset()
    },
    cancelAfterWorkPPIForms: function () {
      var app = this
      app.resetAfterWorkPPIForms()
    },
    deleteAfterWorkPPI: function () {
      var app = this

      app.$store.dispatch('loading/begin_loading')
      if (app.afterWorkPPIForms.id) {
        var ppiId = app.afterWorkPPIForms.id
        session.delete(`/api/userdata/ppi/${ppiId}/`).then(function (response) {
          app.afterWorkPPI = app.afterWorkPPI.filter(ppi => ppi.id !== ppiId)
          app.resetAfterWorkPPIForms()
        }).catch(function (error) {
          var response = error.response
          if (response.status === 500) {
            app.afterWorkPPIForms.showPPIDialog = false
          }
        }).then(function (response) {
          app.$store.dispatch('loading/end_loading')
        })
      }
    },
    loadApi: function () {
      var app = this

      function getActionsCategories (app) {
        return session.get('/api/actions/categories/').catch(function (error) { console.log(error) })
      }

      function getPlaces (app) {
        return session.get('/api/userdata/places/').catch(function (error) { console.log(error) })
      }

      function getAfterWorkPPI (app) {
        return session.get(`/api/userdata/ppi/`).catch(function (error) { console.log(error) })
      }

      function getEnergyList (app) {
        return session.get(`/api/parameters/energies/`).catch(function (error) { console.log(error) })
      }

      function getPPICollections (app) {
        return session.get('/api/userdata/ppi-collections/').catch(function (error) { console.log(error) })
      }

      app.$store.dispatch('loading/begin_loading')

      return axios.all([getActionsCategories(app), getPlaces(app), getAfterWorkPPI(app), getEnergyList(app), getPPICollections(app)])
        .then(axios.spread(function (actionsCategoriesResponse, placesResponse, afterWorkPPIResponse, energyListResponse, ppiCollectionsResponse) {
          var minYear = 2018
          var maxYear = 2050
          var nbYears = maxYear - minYear + 1

          app.actionsCategories = actionsCategoriesResponse.data
          app.energyList = energyListResponse.data
          app.userPlaces = []

          placesResponse.data.forEach(function (place) {
            place.displayName = app.placeChoiceName(place)
            app.userPlaces.push(place)
          })

          app.afterWorkPPI = afterWorkPPIResponse.data
          app.ppiCollections = ppiCollectionsResponse.data || []
          app.userSites = app.userPlaces.filter(place => place.type === 'site')

          // Find "Liste principale" and set it as active tab
          var mainCollection = app.ppiCollections.find(function (col) {
            return col.name === 'Liste principale'
          })
          if (mainCollection) {
            app.mainCollectionId = mainCollection.id
            app.activeCollectionTab = 'tab-collection-' + mainCollection.id
          } else if (app.ppiCollections.length > 0) {
            // Fallback to first collection if "Liste principale" doesn't exist
            app.activeCollectionTab = 'tab-collection-' + app.ppiCollections[0].id
          }

          // Apply initial filter
          app.applyCollectionFilter()

          app.actionsPlaceType = {}
          app.actions = []

          app.actionsCategories.forEach(function (actionCategory) {
            actionCategory.sub_categories.forEach(function (subCategory) {
              subCategory.actions.forEach(function (action) {
                app.actions.push(action)
                app.actionsPlaceType[action.id] = action.place_type
              })
            })
          })
          app.years = Array.from(new Array(nbYears), (val, index) => index + minYear)

          app.$store.dispatch('loading/end_loading')
        }))
    },
    getUserPlace: function (placeId) {
      return this.userPlaces.filter(place => place.id === placeId)[0]
    },
    getAction: function (actionId) {
      return this.actions.filter(action => action.id === actionId)[0]
    },
    getEnergy: function (energyId) {
      return this.energyList.filter(energy => energy.id === energyId)[0]
    },
    downloadTemplate: function () {
      var app = this
      app.$store.dispatch('loading/begin_loading')

      session.get('/api/userdata/ppi/download-template/', {
        responseType: 'arraybuffer'
      }).then(function (response) {
        var filename = 'template_import_ppi.xlsx'
        var disposition = response.request.getResponseHeader('Content-Disposition')

        if (disposition && disposition.indexOf('attachment') !== -1) {
          var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          var matches = filenameRegex.exec(disposition)
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '')
          }
        }

        FileDownload(response.data, filename, response.headers['content-type'])
        app.$store.dispatch('loading/end_loading')
      }).catch(function (error) {
        app.$store.dispatch('loading/end_loading')
        var errorMessage = 'Erreur lors du téléchargement du template'
        if (error && error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error
        }
        app.$store.commit('messages/SHOW', {
          label: 'Erreur',
          description: errorMessage,
          duration: 5000
        })
      })
    },
    handleFileSelect: function (event) {
      var app = this
      var file = event.target.files[0]

      if (!file) {
        return
      }

      if (!file.name.endsWith('.xlsx')) {
        app.$store.commit('messages/SHOW', {
          label: 'Erreur',
          description: 'Veuillez sélectionner un fichier Excel (.xlsx)',
          duration: 5000
        })
        app.$refs.fileInput.value = ''
        return
      }

      // Store file - dialog is already open
      app.pendingFile = file
    },
    openImportDialog: function () {
      var app = this
      // Set the active collection as default
      if (app.activeCollection) {
        app.importOptions.ppiCollectionId = app.activeCollection.id
      } else if (app.ppiCollections.length > 0) {
        // Fallback to first collection if no active collection
        app.importOptions.ppiCollectionId = app.ppiCollections[0].id
      }
      app.showImportDialog = true
    },
    closeImportDialog: function () {
      var app = this
      app.showImportDialog = false
      app.pendingFile = null
      if (app.$refs.fileInput) {
        app.$refs.fileInput.value = ''
      }
    },
    confirmImport: function () {
      var app = this
      if (!app.pendingFile) {
        return
      }

      // Save the currently active collection before import
      var savedCollectionId = app.importOptions.ppiCollectionId

      app.$store.dispatch('loading/begin_loading')

      var formData = new FormData()
      formData.append('file', app.pendingFile)
      // Explicitly convert boolean to string for FormData
      formData.append('delete_existing', app.importOptions.deleteExisting ? 'true' : 'false')
      formData.append('worksheet', 'Feuil1')
      if (app.importOptions.ppiCollectionId) {
        formData.append('ppi_collection_id', app.importOptions.ppiCollectionId)
      }

      session.post('/api/userdata/ppi/import-excel/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(function (response) {
        if (response.data.success) {
          var message = `Import réussi: ${response.data.imported_count} action(s) importée(s)`
          if (response.data.warnings && response.data.warnings.length > 0) {
            message += `. Avertissements: ${response.data.warnings.join(', ')}`
          }
          app.$store.commit('messages/SHOW', {
            label: 'Succès',
            description: message,
            duration: 5000
          })
          // Reload PPI list and collections, then restore the active collection
          app.loadApi().then(function () {
            // Restore the collection that was selected before import
            if (savedCollectionId) {
              var collectionExists = app.ppiCollections.find(function (col) {
                return col.id === savedCollectionId
              })
              if (collectionExists) {
                app.activeCollectionTab = 'tab-collection-' + savedCollectionId
                app.applyCollectionFilter()
              }
            }
          })
        } else {
          // Store errors for modal display
          app.importErrors = {
            title: 'Erreur lors de l\'import',
            errors: response.data.errors || [],
            importedCount: response.data.imported_count || 0
          }
          app.showImportErrorsDialog = true
        }

        // Reset file input and pending file, close dialog
        app.$refs.fileInput.value = ''
        app.pendingFile = null
        app.showImportDialog = false
      }).catch(function (error) {
        var errorMessages = ['Erreur lors de l\'import']
        if (error.response && error.response.data) {
          if (error.response.data.error) {
            errorMessages = [error.response.data.error]
          } else if (error.response.data.errors) {
            if (Array.isArray(error.response.data.errors)) {
              errorMessages = error.response.data.errors
            } else {
              errorMessages = [error.response.data.errors]
            }
          } else if (error.response.data.detail) {
            errorMessages = [error.response.data.detail]
          }
        } else if (error.message) {
          errorMessages = [error.message]
        }
        // Store errors for modal display
        app.importErrors = {
          title: 'Erreur lors de l\'import',
          errors: errorMessages,
          importedCount: 0
        }
        app.showImportErrorsDialog = true
        app.pendingFile = null
        app.showImportDialog = false
      }).then(function () {
        app.$store.dispatch('loading/end_loading')
      })
    },
    renameCollection: function () {
      var app = this
      if (!app.collectionToRename || !app.renamedCollection.name) {
        return
      }

      // Prevent renaming main collection
      if (app.isMainCollection(app.collectionToRename)) {
        app.$store.commit('messages/SHOW', {
          label: 'Erreur',
          description: 'La liste principale ne peut pas être renommée',
          duration: 5000
        })
        app.showRenameCollectionDialog = false
        app.collectionToRename = null
        app.renamedCollection.name = ''
        return
      }

      // Check if name hasn't changed
      if (app.renamedCollection.name === app.collectionToRename.name) {
        app.showRenameCollectionDialog = false
        app.collectionToRename = null
        app.renamedCollection.name = ''
        return
      }

      app.$store.dispatch('loading/begin_loading')
      session.patch(`/api/userdata/ppi-collections/${app.collectionToRename.id}/`, {
        name: app.renamedCollection.name
      })
        .then(function (response) {
          var oldName = app.collectionToRename.name
          var newName = app.renamedCollection.name
          var collectionId = app.collectionToRename.id
          app.showRenameCollectionDialog = false
          app.collectionToRename = null
          app.renamedCollection.name = ''

          // Reload all data to reflect the rename
          app.loadApi().then(function () {
            // Keep the same collection selected after rename
            var collectionExists = app.ppiCollections.find(function (col) {
              return col.id === collectionId
            })
            if (collectionExists) {
              app.activeCollectionTab = 'tab-collection-' + collectionId
              app.applyCollectionFilter()
            }

            app.$store.commit('messages/SHOW', {
              label: 'Succès',
              description: `Liste PPI "${oldName}" renommée en "${newName}"`,
              duration: 3000
            })
          })
        })
        .catch(function (error) {
          var errorMessage = 'Erreur lors du renommage de la liste'
          if (error.response && error.response.data) {
            if (error.response.data.error) {
              errorMessage = error.response.data.error
            } else if (error.response.data.detail) {
              errorMessage = error.response.data.detail
            } else if (error.response.data.name) {
              // Handle field validation errors
              if (Array.isArray(error.response.data.name)) {
                errorMessage = error.response.data.name.join(', ')
              } else {
                errorMessage = error.response.data.name
              }
            }
          }
          app.$store.commit('messages/SHOW', {
            label: 'Erreur',
            description: errorMessage,
            duration: 5000
          })
        })
        .then(function () {
          app.$store.dispatch('loading/end_loading')
        })
    },
    deleteCollection: function () {
      var app = this
      if (!app.collectionToDelete) {
        return
      }

      // Prevent deletion of main collection
      if (app.isMainCollection(app.collectionToDelete)) {
        app.$store.commit('messages/SHOW', {
          label: 'Erreur',
          description: 'La liste principale ne peut pas être supprimée',
          duration: 5000
        })
        app.showDeleteCollectionDialog = false
        app.collectionToDelete = null
        return
      }

      app.$store.dispatch('loading/begin_loading')
      session.delete(`/api/userdata/ppi-collections/${app.collectionToDelete.id}/`)
        .then(function () {
          var deletedName = app.collectionToDelete.name
          app.showDeleteCollectionDialog = false
          app.collectionToDelete = null

          // Reload all data to reflect deletions
          app.loadApi().then(function () {
            // Switch to main collection if we were on the deleted one
            if (app.mainCollectionId) {
              app.activeCollectionTab = 'tab-collection-' + app.mainCollectionId
              app.applyCollectionFilter()
            }

            app.$store.commit('messages/SHOW', {
              label: 'Succès',
              description: `Liste PPI "${deletedName}" et toutes ses actions ont été supprimées avec succès`,
              duration: 3000
            })
          })
        })
        .catch(function (error) {
          var errorMessage = 'Erreur lors de la suppression de la liste'
          if (error.response && error.response.data) {
            if (error.response.data.error) {
              errorMessage = error.response.data.error
            } else if (error.response.data.detail) {
              errorMessage = error.response.data.detail
            }
          }
          app.$store.commit('messages/SHOW', {
            label: 'Erreur',
            description: errorMessage,
            duration: 5000
          })
        })
        .then(function () {
          app.$store.dispatch('loading/end_loading')
        })
    },
    isMainCollection: function (collection) {
      return collection && collection.name === 'Liste principale'
    },
    collectionTabChanged: function (tabId) {
      var app = this
      app.activeCollectionTab = tabId
      app.applyCollectionFilter()
    },
    applyCollectionFilter: function () {
      var app = this
      if (!app.activeCollectionTab) {
        app.filteredAfterWorkPPI = []
        return
      }
      // Extract collection ID from tab ID (format: tab-collection-{id})
      var collectionId = parseInt(app.activeCollectionTab.replace('tab-collection-', ''))
      app.filteredAfterWorkPPI = app.afterWorkPPI.filter(function (ppi) {
        return ppi.ppi_collection === collectionId
      })
    },
    createCollection: function () {
      var app = this
      if (!app.newCollection.name) {
        return
      }

      app.$store.dispatch('loading/begin_loading')
      session.post('/api/userdata/ppi-collections/', {
        name: app.newCollection.name
      }).then(function (response) {
        app.ppiCollections.push(response.data)
        app.showCreateCollectionDialog = false
        var newCollectionName = app.newCollection.name
        app.newCollection = { name: '' }

        // Switch to the newly created collection
        app.activeCollectionTab = 'tab-collection-' + response.data.id
        app.applyCollectionFilter()

        app.$store.commit('messages/SHOW', {
          label: 'Succès',
          description: `Liste PPI "${newCollectionName}" créée avec succès`,
          duration: 3000
        })
      }).catch(function (error) {
        var errorMessage = 'Erreur lors de la création de la liste'
        if (error.response && error.response.data) {
          if (error.response.data.name) {
            errorMessage = error.response.data.name.join(', ')
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error
          }
        }
        app.$store.commit('messages/SHOW', {
          label: 'Erreur',
          description: errorMessage,
          duration: 5000
        })
      }).then(function () {
        app.$store.dispatch('loading/end_loading')
      })
    }
  },
  watch: {
    afterWorkPPI: function () {
      this.applyCollectionFilter()
    }
  }
}
</script>

<style lang="scss" scoped>
.box {
  margin: 1rem 0;
  background-color: white;
  border: 1px solid #eee;
  padding: 1rem;

  h2 {
    margin: 0;
    font-weight: normal;
  }

  h2 {
    font-size: 1.1rem;
  }
}

.ppi_list__wrapper {
  padding: 3rem;
  overflow: auto;
  height: calc(100vh - 64 * 2px);
}

.header-section {
  margin-bottom: 1.5rem;
  align-items: center;
}

.import-section-header {
  text-align: right;
  display: flex;
  justify-content: flex-end;
}

.import-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.tabs-with-button {
  display: flex;
  align-items: flex-end;
  margin-bottom: 1rem;
  gap: 0;
  position: relative;
}

.ppi-collections-tabs {
  flex: 0 1 auto;
}

.add-collection-button {
  flex-shrink: 0;
  margin: 0;
  margin-left: 0.25rem;
  margin-bottom: 0.5rem;
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
}

.add-collection-button .md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.rename-collection-button {
  position: absolute;
  right: 2.5rem;
  margin: 0;
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
}

.rename-collection-button .md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.delete-collection-button {
  position: absolute;
  right: 0rem;
  margin: 0;
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
}

.delete-collection-button .md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.tab-delete-button-injected {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.tab-delete-button-injected:hover {
  opacity: 1;
}

.tab-label-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.tab-delete-button {
  width: 20px;
  height: 20px;
  min-width: 20px;
  padding: 0;
  margin: 0;
  margin-left: 0.25rem;
}

.tab-delete-button .md-icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.delete-collection-button,
.rename-collection-button,
.add-collection-button {
  flex-shrink: 0;
  margin: 0;
  margin-bottom: 0.5rem;
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
}

.delete-collection-button .md-icon,
.rename-collection-button .md-icon,
.add-collection-button .md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.warning-text {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #856404;
  font-size: 0.9rem;

  md-icon {
    color: #ffc107;
  }
}

.info-text {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background-color: #d1ecf1;
  border-left: 4px solid #17a2b8;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #0c5460;
  font-size: 0.9rem;

  md-icon {
    color: #17a2b8;
  }
}

.import-result-floating {
  position: fixed;
  top: 80px;
  right: 20px;
  max-width: 400px;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  animation: slideIn 0.3s ease-out;
}

.import-result-floating .close-button {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1001;
  min-width: 32px;
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.9);
}

.import-result-floating .success-message,
.import-result-floating .error-message {
  padding-right: 2.5rem;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.bg-gray {
  background: darken(white, 2);
}
.zebra {
  border: 1px solid #eee;
  tbody {
    tr:nth-child(even) {
      background: #f2f5f5;
    }
  }
}

.dashed__card {
  border: 1px dashed #ccc;
  margin-bottom: 10px;
}

.dashed__card >>> .md-card-content {
  background: rgb(248, 248, 248);
}

.import-result {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 4px;
}

.success-message {
  color: #4caf50;
  background-color: #e8f5e9;
  padding: 1rem;
  border-radius: 4px;
}

.error-message {
  color: #f44336;
  background-color: #ffebee;
  padding: 1rem;
  border-radius: 4px;
}

.error-message ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

</style>



// WEBPACK FOOTER //
// src/views/PPIList.vue